import { Box, Card, CardActionArea, CardContent, Skeleton, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { userApi } from '../../services/userApi';
import { UnverifiedEmailBanner } from '../../components/auth/UnverifiedEmailBanner';

interface SummaryCardProps {
  label: string;
  count: number | undefined;
  to: string;
  isLoading: boolean;
}

function SummaryCard({ label, count, to, isLoading }: SummaryCardProps) {
  const ariaLabel = isLoading
    ? `${label} (loading)`
    : `${label} (${count ?? 0}) — open ${label.toLowerCase()}`;

  return (
    <Card variant="outlined" sx={{ flex: 1, minWidth: 220 }}>
      <CardActionArea component={RouterLink} to={to} aria-label={ariaLabel}>
        <CardContent>
          <Typography variant="overline" component="p" color="text.secondary">
            {label}
          </Typography>
          {isLoading ? (
            <Skeleton variant="text" width="40%" height={48} aria-hidden="true" />
          ) : (
            <Typography variant="h2" component="p" sx={{ fontSize: '2.25rem', fontWeight: 700 }}>
              {count ?? 0}
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

/**
 * Dashboard landing page. Three at-a-glance cards link out to the
 * detail pages. Each card uses CardActionArea so it's a single
 * keyboard-focusable target with a descriptive aria-label (WCAG 2.4.4).
 */
export function DashboardHomePage() {
  const alertsQuery = useQuery({
    queryKey: ['user', 'alerts'],
    queryFn: () => userApi.listAlerts(),
  });
  const savedSearchesQuery = useQuery({
    queryKey: ['user', 'saved-searches'],
    queryFn: () => userApi.listSavedSearches(),
  });
  const watchlistQuery = useQuery({
    queryKey: ['user', 'watchlist'],
    queryFn: () => userApi.listWatchlist(),
  });

  const activeAlerts = alertsQuery.data?.alerts.filter((a) => a.isActive).length;

  return (
    <Box>
      <UnverifiedEmailBanner />
      <Typography variant="h1" component="h1" sx={{ fontSize: '1.6rem', mb: 1 }}>
        Welcome back
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage your alerts, saved searches, watchlist, and privacy settings.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <SummaryCard
          label="Active alerts"
          count={activeAlerts}
          to="/dashboard/alerts"
          isLoading={alertsQuery.isLoading}
        />
        <SummaryCard
          label="Saved searches"
          count={savedSearchesQuery.data?.savedSearches.length}
          to="/dashboard/saved-searches"
          isLoading={savedSearchesQuery.isLoading}
        />
        <SummaryCard
          label="Watchlist"
          count={watchlistQuery.data?.watchlist.length}
          to="/dashboard/watchlist"
          isLoading={watchlistQuery.isLoading}
        />
      </Stack>
    </Box>
  );
}

export default DashboardHomePage;
